import React, { useState, useEffect, useMemo, useCallback } from 'react';
import TrashTab from './TrashTab';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import {
  FiSearch,
  FiUser,
  FiUsers,
  FiX,
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiChevronRight,
  FiChevronLeft,
  FiFolder,
  FiRefreshCw,
  FiChevronDown,
  FiUserPlus,
  FiMail,
  FiLock,
  FiGitBranch,
  FiRotateCcw,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI, hrAPI, tasksAPI } from '../services/api';
import '../styles/AdminView.css';


// ================= CONSTANTS =================

const STATUS_COLORS = {
  'Working': '#10b981', 'On Break': '#f59e0b',
  'Busy': '#ef4444', 'Not Available': '#6b7280',
};
const ROLE_LABELS = { admin: 'Admin', manager: 'Manager', hr: 'HR', employee: 'Employee' };
const PROJECT_COLORS = ['#4f46e5', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed', '#db2777', '#2563eb'];


// ================= DRAGGABLE PERSON BOX =================

const PersonBox = ({ user, isDragOverlay = false }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `person-${user._id}`,
    data: { type: 'person', userId: user._id, user },
    disabled: isDragOverlay,
  });

  return (
    <div
      ref={!isDragOverlay ? setNodeRef : undefined}
      {...(!isDragOverlay ? attributes : {})}
      {...(!isDragOverlay ? listeners : {})}
      className={`person-box ${isDragging ? 'dragging' : ''} ${isDragOverlay ? 'drag-overlay' : ''}`}
      title={`${user.username} (${user.email})`}
    >
      <div className="person-avatar"><FiUser size={14} /></div>
      <div className="person-details">
        <span className="person-name">{user.username}</span>
        <span className={`person-role-badge role-${user.role}`}>{ROLE_LABELS[user.role] || user.role}</span>
      </div>
      <span className="person-status-dot" style={{ background: STATUS_COLORS[user.status] || '#6b7280' }} />
    </div>
  );
};


// ================= DRAGGABLE + DROPPABLE PROJECT CARD =================

const ProjectCard = ({ project, onEdit, onDelete, onRemoveMember, onUpdateLeadership, allUsers = [], isDragOverlay = false }) => {
  const [editingField, setEditingField] = useState(null); // 'secondManager' | 'hr' | null

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: `drag-project-${project._id}`,
    data: { type: 'drag-project', projectId: project._id, project },
    disabled: isDragOverlay,
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `project-${project._id}`,
    data: { type: 'project', projectId: project._id },
  });
  const { setNodeRef: setSecondManagerDropRef, isOver: isOverSecondManager } = useDroppable({
    id: `project-${project._id}-secondManager`,
    data: { type: 'leadership-secondManager', projectId: project._id },
  });
  const { setNodeRef: setHrDropRef, isOver: isOverHr } = useDroppable({
    id: `project-${project._id}-hr`,
    data: { type: 'leadership-hr', projectId: project._id },
  });

  const mergedRef = (node) => {
    if (!isDragOverlay) setDragRef(node);
    setDropRef(node);
  };

  const managers = allUsers.filter(u => ['manager', 'admin', 'employee'].includes(u.role));
  const hrUsers = allUsers.filter(u => u.role === 'hr');

  const handleLeadershipChange = (field, value) => {
    if (onUpdateLeadership) {
      onUpdateLeadership(project._id, { [field]: value || null });
    }
    setEditingField(null);
  };

  const secondMgr = project.secondManagerId;
  const assignedHr = project.hrId;

  return (
    <div
      ref={mergedRef}
      {...(!isDragOverlay ? attributes : {})}
      {...(!isDragOverlay ? listeners : {})}
      className={`project-card ${isOver ? 'drop-hover' : ''} ${isDragging ? 'dragging' : ''} ${isDragOverlay ? 'drag-overlay-project' : ''}`}
      style={{ borderLeftColor: project.color || '#4f46e5', opacity: isDragging ? 0.4 : 1 }}
    >
      <div className="project-card-header">
        <FiFolder size={14} style={{ color: project.color }} />
        <span className="project-card-name">{project.name}</span>
        {!isDragOverlay && (
          <div className="project-card-actions">
            <button onClick={(e) => { e.stopPropagation(); onEdit(project); }} title="Edit"><FiEdit2 size={12} /></button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(project._id); }} title="Delete"><FiTrash2 size={12} /></button>
          </div>
        )}
      </div>

      {/* Leadership Section */}
      {!isDragOverlay && (
        <div className="project-leadership">
          <div ref={setSecondManagerDropRef} className={`leadership-row ${isOverSecondManager ? 'drop-active' : ''}`}>
            <span className="leadership-label">2nd Manager</span>
            {editingField === 'secondManager' ? (
              <select
                className="leadership-select"
                defaultValue={secondMgr?._id || secondMgr || ''}
                autoFocus
                onChange={(e) => handleLeadershipChange('secondManagerId', e.target.value)}
                onBlur={() => setEditingField(null)}
                onClick={(e) => e.stopPropagation()}
              >
                <option value="">None</option>
                {managers.filter(m => String(m._id) !== String(project.managerId?._id || project.managerId)).map(m => (
                  <option key={m._id} value={m._id}>{m.username}</option>
                ))}
              </select>
            ) : (
              <span
                className={`leadership-value ${secondMgr ? '' : 'empty'}`}
                onClick={(e) => { e.stopPropagation(); setEditingField('secondManager'); }}
                title="Click or drag person here to assign"
              >
                {secondMgr?.username || '+ Assign'}
              </span>
            )}
          </div>
          <div ref={setHrDropRef} className={`leadership-row ${isOverHr ? 'drop-active' : ''}`}>
            <span className="leadership-label">HR</span>
            {editingField === 'hr' ? (
              <select
                className="leadership-select"
                defaultValue={assignedHr?._id || assignedHr || ''}
                autoFocus
                onChange={(e) => handleLeadershipChange('hrId', e.target.value)}
                onBlur={() => setEditingField(null)}
                onClick={(e) => e.stopPropagation()}
              >
                <option value="">None</option>
                {hrUsers.map(h => (
                  <option key={h._id} value={h._id}>{h.username}</option>
                ))}
              </select>
            ) : (
              <span
                className={`leadership-value ${assignedHr ? '' : 'empty'}`}
                onClick={(e) => { e.stopPropagation(); setEditingField('hr'); }}
                title="Click or drag person here to assign"
              >
                {assignedHr?.username || '+ Assign'}
              </span>
            )}
          </div>
        </div>
      )}
      {project.tasks && project.tasks.length > 0 && (
        <div className="project-task-count">{project.tasks.length} task{project.tasks.length !== 1 ? 's' : ''}</div>
      )}
      {project.members && project.members.length > 0 && (
        <div className="project-employees">
          {project.members.map(m => (
            <div key={m._id} className="project-employee">
              <div className="project-employee-avatar"><FiUser size={12} /></div>
              <div className="project-employee-info">
                <span className="project-employee-name">{m.username}</span>
                <span className={`person-role-badge role-${m.role}`}>{ROLE_LABELS[m.role] || m.role}</span>
              </div>
              <span className="person-status-dot" style={{ background: STATUS_COLORS[m.status] || '#6b7280' }} />
              {!isDragOverlay && <button className="employee-remove" onClick={(e) => { e.stopPropagation(); onRemoveMember(project._id, m._id); }} title="Remove"><FiX size={10} /></button>}
            </div>
          ))}
        </div>
      )}
      {(!project.members || project.members.length === 0) && (
        <div className="project-empty-drop">Drop employees here</div>
      )}
    </div>
  );
};


// ================= DROPPABLE TREE NODE =================

const TreeNode = ({ user, children, projects, level, onEditProject, onDeleteProject, onRemoveMember, onUpdateLeadership, allUsers }) => {
  const [expanded, setExpanded] = useState(true);
  const isManagerOrAdmin = user.role === 'manager' || user.role === 'admin';

  const { setNodeRef, isOver } = useDroppable({
    id: `manager-${user._id}`,
    data: { type: 'manager', userId: user._id, user },
    disabled: !isManagerOrAdmin,
  });

  const treeChildren = children.filter(c => c.user.role !== 'employee');
  const hasChildren = treeChildren.length > 0;
  const userProjects = projects.filter(p => String(p.managerId?._id || p.managerId) === String(user._id));
  const hasContent = hasChildren || userProjects.length > 0;

  return (
    <div className="tree-node-wrapper">
      <div className={`tree-node level-${Math.min(level, 3)}`}>
        <div
          ref={isManagerOrAdmin ? setNodeRef : undefined}
          className={`tree-node-header ${isOver ? 'drop-target-active' : ''}`}
          onClick={() => hasContent && setExpanded(!expanded)}
        >
          <div className="tree-node-avatar" style={{
            background: user.role === 'admin' ? '#fee2e2' : user.role === 'manager' ? '#fef3c7' : '#dbeafe'
          }}>
            <FiUser size={16} />
          </div>
          <div className="tree-node-info">
            <span className="tree-node-name">{user.username}</span>
            <span className={`role-badge role-${user.role}`}>{ROLE_LABELS[user.role] || user.role}</span>
            <span className="tree-node-status" style={{ background: STATUS_COLORS[user.status] || '#6b7280' }} />
          </div>
          {hasContent && (
            <span className="tree-expand-icon">{expanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}</span>
          )}
        </div>

        {expanded && userProjects.length > 0 && (
          <div className="tree-projects">
            {userProjects.map(p => (
              <ProjectCard key={p._id} project={p} onEdit={onEditProject} onDelete={onDeleteProject} onRemoveMember={onRemoveMember} onUpdateLeadership={onUpdateLeadership} allUsers={allUsers} />
            ))}
          </div>
        )}
      </div>

      {expanded && hasChildren && (
        <div className="tree-children">
          {treeChildren.map(child => (
            <div key={child.user._id} className="tree-child-branch">
              <div className="tree-line-v" />
              <div className="tree-line-h" />
              <TreeNode
                user={child.user} children={child.children} projects={projects} level={level + 1}
                onEditProject={onEditProject} onDeleteProject={onDeleteProject} onRemoveMember={onRemoveMember}
                onUpdateLeadership={onUpdateLeadership} allUsers={allUsers}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ================= PROJECT MODAL =================

const ProjectModal = ({ isOpen, onClose, project, users, onSave }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [managerId, setManagerId] = useState('');
  const [secondManagerId, setSecondManagerId] = useState('');
  const [hrId, setHrId] = useState('');
  const [color, setColor] = useState('#4f46e5');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (project) {
      setName(project.name || ''); setDescription(project.description || '');
      setManagerId(project.managerId?._id || project.managerId || '');
      setSecondManagerId(project.secondManagerId?._id || project.secondManagerId || '');
      setHrId(project.hrId?._id || project.hrId || '');
      setColor(project.color || '#4f46e5');
    } else {
      setName(''); setDescription(''); setManagerId(''); setSecondManagerId(''); setHrId(''); setColor('#4f46e5');
    }
  }, [project, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave({ name, description, managerId: managerId || null, secondManagerId: secondManagerId || null, hrId: hrId || null, color, _id: project?._id });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;
  const managers = users.filter(u => ['manager', 'admin'].includes(u.role));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content project-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{project ? 'Edit Project' : 'New Project'}</h2>
          <button className="close-btn" onClick={onClose}><FiX size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="task-form">
          <div className="form-group">
            <label>Project Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Website Redesign" required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Manager</label>
              <select value={managerId} onChange={e => setManagerId(e.target.value)}>
                <option value="">None</option>
                {managers.map(m => <option key={m._id} value={m._id}>{m.username}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Secondary Manager</label>
              <select value={secondManagerId} onChange={e => setSecondManagerId(e.target.value)}>
                <option value="">None</option>
                {users.filter(u => ['admin', 'manager', 'employee'].includes(u.role)).map(m => <option key={m._id} value={m._id}>{m.username}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>HR</label>
              <select value={hrId} onChange={e => setHrId(e.target.value)}>
                <option value="">None</option>
                {users.filter(u => u.role === 'hr').map(h => <option key={h._id} value={h._id}>{h.username}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Color</label>
              <div className="color-picker">
                {PROJECT_COLORS.map(c => (
                  <button key={c} type="button" className={`color-dot ${color === c ? 'active' : ''}`} style={{ background: c }} onClick={() => setColor(c)} />
                ))}
              </div>
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="submit-btn" disabled={saving}>{saving ? 'Saving...' : project ? 'Update' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};


// ================= USER MODAL (Create / Edit) =================

const UserModal = ({ isOpen, onClose, user, allUsers, onSave, isHR = false }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('employee');
  const [managerId, setManagerId] = useState('');
  const [department, setDepartment] = useState('');
  const [saving, setSaving] = useState(false);

  const isEdit = !!user;

  useEffect(() => {
    if (user) {
      setUsername(user.username || '');
      setEmail(user.email || '');
      setPassword('');
      setRole(user.role || 'employee');
      setManagerId(user.managerId ? String(user.managerId) : '');
      setDepartment(user.department || '');
    } else {
      setUsername(''); setEmail(''); setPassword(''); setRole('employee'); setManagerId(''); setDepartment('');
    }
  }, [user, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        _id: user?._id,
        username, email, password: password || undefined,
        role, managerId: managerId || null, department,
      });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;
  const managers = allUsers.filter(u => ['manager', 'admin'].includes(u.role) && u._id !== user?._id);
  // HR cannot create/assign admin role
  const availableRoles = isHR
    ? Object.entries(ROLE_LABELS).filter(([val]) => val !== 'admin')
    : Object.entries(ROLE_LABELS);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content user-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit User' : 'Create User'}</h2>
          <button className="close-btn" onClick={onClose}><FiX size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="task-form">
          <div className="form-row">
            <div className="form-group">
              <label><FiUser size={14} /> Username *</label>
              <input value={username} onChange={e => setUsername(e.target.value)} placeholder="johndoe" required minLength={3} disabled={isEdit} />
            </div>
            <div className="form-group">
              <label><FiMail size={14} /> Email *</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="john@company.com" required disabled={isEdit} />
            </div>
          </div>
          {!isEdit && (
            <div className="form-group">
              <label><FiLock size={14} /> Password *</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" required minLength={6} />
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label>Role</label>
              <select value={role} onChange={e => setRole(e.target.value)}>
                {availableRoles.map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label><FiGitBranch size={14} /> Reports To</label>
              <select value={managerId} onChange={e => setManagerId(e.target.value)}>
                <option value="">None</option>
                {managers.map(m => <option key={m._id} value={m._id}>{m.username}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Department</label>
            <input value={department} onChange={e => setDepartment(e.target.value)} placeholder="e.g. Engineering" />
          </div>
          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="submit-btn" disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};


// ================= PEOPLE TAB =================

const PeopleTab = ({ users, onRefresh, allUsers, isHR = false, apiService }) => {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [editingManagerId, setEditingManagerId] = useState(null);

  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      if (roleFilter && u.role !== roleFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return u.username.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || (u.department || '').toLowerCase().includes(q);
    });
  }, [users, search, roleFilter]);

  const handleSaveUser = async (data) => {
    if (data._id) {
      // Edit: update role, manager, department via separate endpoints
      const promises = [];
      const original = users.find(u => u._id === data._id);
      if (original && data.role !== original.role) promises.push(apiService.updateUserRole(data._id, data.role));
      if (original && String(data.managerId || '') !== String(original.managerId || '')) promises.push(apiService.updateUserManager(data._id, data.managerId));
      if (original && data.department !== (original.department || '')) promises.push(apiService.updateUserDepartment(data._id, data.department));
      await Promise.all(promises);
      toast.success('User updated');
    } else {
      await apiService.createUser(data);
      toast.success('User created');
    }
    onRefresh();
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Delete user "${username}"? This cannot be undone.`)) return;
    try {
      await apiService.deleteUser(userId);
      toast.success('User deleted');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to delete'); }
  };

  const handleInlineRoleChange = async (userId, newRole) => {
    try {
      await apiService.updateUserRole(userId, newRole);
      toast.success('Role updated');
      setEditingRoleId(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to update role'); }
  };

  const handleInlineManagerChange = async (userId, newManagerId) => {
    try {
      await apiService.updateUserManager(userId, newManagerId || null);
      toast.success('Manager updated');
      setEditingManagerId(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to update manager'); }
  };

  const managers = users.filter(u => ['manager', 'admin'].includes(u.role));

  return (
    <div className="people-tab">
      <div className="people-tab-header">
        <div className="people-tab-filters">
          <div className="sidebar-search people-search">
            <FiSearch size={14} />
            <input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} />
            {search && <button className="clear-search" onClick={() => setSearch('')}><FiX size={12} /></button>}
          </div>
          <select className="people-role-filter" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
            <option value="">All Roles</option>
            {Object.entries(ROLE_LABELS).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
        </div>
        <button className="create-project-btn" onClick={() => { setEditingUser(null); setUserModalOpen(true); }}>
          <FiUserPlus size={16} /> New User
        </button>
      </div>

      <div className="people-table-wrap">
        <table className="people-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Role</th>
              <th>Reports To</th>
              <th>Department</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(u => (
              <tr key={u._id}>
                <td>
                  <div className="people-table-user">
                    <div className="people-table-avatar" style={{
                      background: u.role === 'admin' ? '#fee2e2' : u.role === 'manager' ? '#fef3c7' : '#dbeafe'
                    }}><FiUser size={14} /></div>
                    <span>{u.username}</span>
                  </div>
                </td>
                <td className="people-table-email">{u.email}</td>
                <td>
                  {isHR && u.role === 'admin' ? (
                    <span className={`role-badge role-${u.role}`}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  ) : editingRoleId === u._id ? (
                    <select
                      className="inline-select"
                      defaultValue={u.role}
                      autoFocus
                      onChange={e => handleInlineRoleChange(u._id, e.target.value)}
                      onBlur={() => setEditingRoleId(null)}
                    >
                      {(isHR ? Object.entries(ROLE_LABELS).filter(([val]) => val !== 'admin') : Object.entries(ROLE_LABELS)).map(([val, label]) => (
                        <option key={val} value={val}>{label}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`role-badge role-${u.role} clickable`} onClick={() => setEditingRoleId(u._id)}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  )}
                </td>
                <td>
                  {isHR && u.role === 'admin' ? (
                    <span className="people-table-manager">
                      {u.managerId ? (users.find(x => x._id === String(u.managerId))?.username || '—') : '—'}
                    </span>
                  ) : editingManagerId === u._id ? (
                    <select
                      className="inline-select"
                      defaultValue={u.managerId ? String(u.managerId) : ''}
                      autoFocus
                      onChange={e => handleInlineManagerChange(u._id, e.target.value)}
                      onBlur={() => setEditingManagerId(null)}
                    >
                      <option value="">None</option>
                      {managers.filter(m => m._id !== u._id).map(m => (
                        <option key={m._id} value={m._id}>{m.username}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="people-table-manager clickable" onClick={() => setEditingManagerId(u._id)}>
                      {u.managerId ? (users.find(x => x._id === String(u.managerId))?.username || '—') : '—'}
                    </span>
                  )}
                </td>
                <td className="people-table-dept">{u.department || '—'}</td>
                <td>
                  <span className="people-table-status" style={{ background: STATUS_COLORS[u.status] || '#6b7280' }} />
                  <span className="people-table-status-text">{u.status}</span>
                </td>
                <td>
                  {isHR && u.role === 'admin' ? (
                    <div className="people-table-actions">
                      <span className="admin-protected-label" title="Admin profiles are read-only for HR">🔒</span>
                    </div>
                  ) : (
                    <div className="people-table-actions">
                      <button onClick={() => { setEditingUser(u); setUserModalOpen(true); }} title="Edit"><FiEdit2 size={14} /></button>
                      <button onClick={() => handleDeleteUser(u._id, u.username)} title="Delete" className="danger-action"><FiTrash2 size={14} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr><td colSpan={7} className="people-table-empty">No users found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <UserModal isOpen={userModalOpen} onClose={() => { setUserModalOpen(false); setEditingUser(null); }}
        user={editingUser} allUsers={allUsers} onSave={handleSaveUser} isHR={isHR} />
    </div>
  );
};


// ================= TRASH TAB =================
// (Imported from TrashTab.jsx)



// ================= MAIN =================

const AdminView = () => {
  const { user: currentUser } = useAuth();
  const isHR = currentUser?.role === 'hr';
  const apiService = isHR ? hrAPI : adminAPI;
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarSearch, setSidebarSearch] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [activeTab, setActiveTab] = useState('org');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  const loadData = useCallback(async () => {
    try {
      const [orgRes, projRes, taskRes] = await Promise.all([
        apiService.getOrgTree(), apiService.getProjects(), apiService.getTasks(),
      ]);
      setUsers(orgRes.data.users || []);
      setProjects(projRes.data.projects || []);
      setTasks(taskRes.data.tasks || []);
    } catch (err) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [apiService]);

  useEffect(() => { loadData(); }, [loadData]);

  // Build tree from non-employee users; employees are shown inside project cards
  const tree = useMemo(() => {
    const map = {};
    const roots = [];
    users.forEach(u => { map[u._id] = { user: u, children: [] }; });
    users.forEach(u => {
      if (u.role === 'employee') return; // skip employees in tree
      const mid = u.managerId ? String(u.managerId) : null;
      if (mid && map[mid]) map[mid].children.push(map[u._id]);
      else roots.push(map[u._id]);
    });
    const roleOrder = { admin: 0, manager: 1, hr: 2, employee: 3 };
    const sortN = (nodes) => { nodes.sort((a, b) => (roleOrder[a.user.role] || 9) - (roleOrder[b.user.role] || 9)); nodes.forEach(n => sortN(n.children)); };
    sortN(roots);
    return roots;
  }, [users]);

  // Employees not assigned to any project
  const unassignedEmployees = useMemo(() => {
    const assignedIds = new Set();
    projects.forEach(p => (p.members || []).forEach(m => assignedIds.add(String(m._id || m))));
    return users.filter(u => u.role === 'employee' && !assignedIds.has(String(u._id)));
  }, [users, projects]);

  const sidebarPeople = useMemo(() => {
    return users.filter(u => {
      if (!sidebarSearch) return true;
      const q = sidebarSearch.toLowerCase();
      return u.username.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    });
  }, [users, sidebarSearch]);

  const handleDragStart = (e) => setActiveId(e.active.id);
  const handleDragEnd = async (e) => {
    const { active, over } = e;
    setActiveId(null);
    if (!over) return;
    const dragData = active.data?.current;
    const dropData = over.data?.current;

    // Person dropped onto a leadership slot
    if (dragData?.type === 'person' && (dropData?.type === 'leadership-secondManager' || dropData?.type === 'leadership-hr')) {
      const field = dropData.type === 'leadership-secondManager' ? 'secondManagerId' : 'hrId';
      const user = users.find(u => String(u._id) === String(dragData.userId));

      if (!user) {
        toast.error('User not found');
        return;
      }

      // Validate roles
      if (field === 'secondManagerId' && !['manager', 'admin', 'employee'].includes(user.role)) {
        toast.error('Only Managers, Admins, or Employees can be Secondary Managers'); return;
      }
      if (field === 'hrId' && user.role !== 'hr') {
        toast.error('Only HR users can be assigned to the HR role'); return;
      }

      await handleUpdateLeadership(dropData.projectId, { [field]: dragData.userId });
      toast.success('Leadership updated');
      return;
    }

    // Person dropped onto a project -> add member
    if (dragData?.type === 'person' && dropData?.type === 'project') {
      const proj = projects.find(p => p._id === dropData.projectId);
      if (proj?.members?.some(m => String(m._id || m) === String(dragData.userId))) {
        toast.info('Already a member'); return;
      }
      try {
        await apiService.addProjectMember(dropData.projectId, dragData.userId);
        toast.success('Member added');
        loadData();
      } catch (err) { toast.error('Failed to add member'); }
    }

    // Project dropped onto a manager/admin node -> reassign project
    if (dragData?.type === 'drag-project' && dropData?.type === 'manager') {
      const proj = projects.find(p => p._id === dragData.projectId);
      const currentManagerId = String(proj?.managerId?._id || proj?.managerId);
      const newManagerId = String(dropData.userId);
      if (currentManagerId === newManagerId) {
        toast.info('Project already belongs to this manager'); return;
      }
      try {
        await apiService.updateProject(dragData.projectId, { managerId: newManagerId });
        toast.success(`Project moved to ${dropData.user?.username || 'manager'}`);
        loadData();
      } catch (err) { toast.error('Failed to reassign project'); }
    }
  };

  const handleSaveProject = async (data) => {
    if (data._id) { await apiService.updateProject(data._id, data); toast.success('Project updated'); }
    else { await apiService.createProject(data); toast.success('Project created'); }
    loadData();
  };
  const handleDeleteProject = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try { await apiService.deleteProject(id); toast.success('Deleted'); loadData(); } catch { toast.error('Failed'); }
  };
  const handleRemoveMember = async (pid, uid) => {
    try { await apiService.removeProjectMember(pid, uid); toast.success('Removed'); loadData(); } catch { toast.error('Failed'); }
  };
  const handleUpdateLeadership = async (pid, data) => {
    try { await apiService.updateProject(pid, data); loadData(); } catch { toast.error('Failed to update leadership'); }
  };

  const activeDragUser = activeId ? users.find(u => `person-${u._id}` === activeId) : null;
  const activeDragProject = activeId ? projects.find(p => `drag-project-${p._id}` === activeId) : null;

  if (loading) return <div className="admin-container"><div className="admin-loading"><div className="spinner" /><span>Loading...</span></div></div>;

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-title-row">
            <h1>Organization</h1>
            <div className="admin-header-actions">
              {activeTab === 'org' && (
                <button className="create-project-btn" onClick={() => { setEditingProject(null); setProjectModalOpen(true); }}>
                  <FiPlus size={16} /> New Project
                </button>
              )}
              <button className="refresh-btn" onClick={() => { setLoading(true); loadData(); }}><FiRefreshCw size={16} /></button>
            </div>
          </div>
          <div className="admin-stats-row">
            <span className="stat-pill">{users.length} People</span>
            <span className="stat-pill">{projects.length} Projects</span>
            <span className="stat-pill">{users.filter(u => u.role === 'manager').length} Managers</span>
            <span className="stat-pill">{users.filter(u => u.role === 'employee').length} Employees</span>
          </div>
          <div className="admin-tabs">
            <button className={`admin-tab ${activeTab === 'org' ? 'active' : ''}`} onClick={() => setActiveTab('org')}>
              <FiGitBranch size={15} /> Organization
            </button>
            <button className={`admin-tab ${activeTab === 'people' ? 'active' : ''}`} onClick={() => setActiveTab('people')}>
              <FiUsers size={15} /> People
            </button>
          </div>
        </div>

        {activeTab === 'org' && (
          <div className="admin-body">
            <div className="admin-tree-area">
              <div className="tree-scroll">
                {tree.map(node => (
                  <TreeNode key={node.user._id} user={node.user} children={node.children} projects={projects} level={0}
                    onEditProject={(p) => { setEditingProject(p); setProjectModalOpen(true); }}
                    onDeleteProject={handleDeleteProject} onRemoveMember={handleRemoveMember}
                    onUpdateLeadership={handleUpdateLeadership} allUsers={users} />
                ))}
                {projects.filter(p => !p.managerId).length > 0 && (
                  <div className="unassigned-projects">
                    <h2 className="unassigned-title"><FiFolder size={14} /> Unassigned Projects</h2>
                    {projects.filter(p => !p.managerId).map(p => (
                      <ProjectCard key={p._id} project={p}
                        onEdit={(pr) => { setEditingProject(pr); setProjectModalOpen(true); }}
                        onDelete={handleDeleteProject} onRemoveMember={handleRemoveMember}
                        onUpdateLeadership={handleUpdateLeadership} allUsers={users} />
                    ))}
                  </div>
                )}
                {unassignedEmployees.length > 0 && (
                  <div className="unassigned-projects">
                    <h2 className="unassigned-title"><FiUsers size={14} /> Unassigned Employees</h2>
                    <div className="unassigned-employees-grid">
                      {unassignedEmployees.map(u => (
                        <div key={u._id} className="project-employee unassigned">
                          <div className="project-employee-avatar"><FiUser size={12} /></div>
                          <div className="project-employee-info">
                            <span className="project-employee-name">{u.username}</span>
                            <span className="person-role-badge role-employee">Employee</span>
                          </div>
                          <span className="person-status-dot" style={{ background: STATUS_COLORS[u.status] || '#6b7280' }} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {tree.length === 0 && <div className="empty-tree"><FiUsers size={32} /><p>No users found</p></div>}
              </div>
            </div>

            {/* Collapsible People Sidebar */}
            <div className={`people-sidebar ${sidebarOpen ? 'open' : 'collapsed'}`}>
              <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
                {sidebarOpen ? <FiChevronRight size={16} /> : <FiChevronLeft size={16} />}
              </button>
              {sidebarOpen && (
                <>
                  <div className="sidebar-header">
                    <h2><FiUsers size={16} /> People</h2>
                    <span className="sidebar-count">{sidebarPeople.length}</span>
                  </div>
                  <div className="sidebar-search">
                    <FiSearch size={14} />
                    <input placeholder="Search..." value={sidebarSearch} onChange={e => setSidebarSearch(e.target.value)} />
                    {sidebarSearch && <button className="clear-search" onClick={() => setSidebarSearch('')}><FiX size={12} /></button>}
                  </div>
                  <div className="sidebar-people-list">
                    {sidebarPeople.map(u => <PersonBox key={u._id} user={u} />)}
                    {sidebarPeople.length === 0 && <div className="sidebar-empty">No matches</div>}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'people' && (
          <PeopleTab users={users} allUsers={users} onRefresh={loadData} isHR={isHR} apiService={apiService} />
        )}

        <DragOverlay>
          {activeDragUser && <PersonBox user={activeDragUser} isDragOverlay />}
          {activeDragProject && (
            <ProjectCard project={activeDragProject} isDragOverlay onEdit={() => { }} onDelete={() => { }} onRemoveMember={() => { }}
              onUpdateLeadership={() => { }} allUsers={users} />
          )}
        </DragOverlay>
        <ProjectModal isOpen={projectModalOpen} onClose={() => { setProjectModalOpen(false); setEditingProject(null); }}
          project={editingProject} users={users} onSave={handleSaveProject} />
      </div>
    </DndContext>
  );
};

export default AdminView;
